import { useContext, useEffect, useRef, useState } from "react";
import { EnteredContext } from "../EnteredContext";

// Glyphs the characters flicker through while "decoding".
const GLYPHS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789/\\<>*#{}[]";

function prefersReduced() {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function randGlyph() {
  return GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
}

/**
 * Renders `text` with a two-stage HUD reveal, once it's visible (splash
 * dismissed) and scrolled into view:
 *   1. FILL — random characters load in left-to-right up to full length
 *   2. DESCRAMBLE — those random characters resolve left-to-right into the text
 * Runs once. Respects prefers-reduced-motion.
 *
 * Props: text, as (element tag, default "span"), className, duration (ms).
 */
export default function DecodeText({ text, as: Tag = "span", className = "", duration = 850, delay = 0 }) {
  const [display, setDisplay] = useState(() => (prefersReduced() ? text : ""));
  const [decoding, setDecoding] = useState(false);
  const ref = useRef(null);
  const startedRef = useRef(false);
  const ready = useContext(EnteredContext);

  useEffect(() => {
    const el = ref.current;
    if (!el || !ready) return undefined;

    if (prefersReduced()) {
      setDisplay(text);
      return undefined;
    }

    let timer;

    const L = text.length;
    const fillDur = duration * 0.4; // random characters load in
    const descrambleDur = duration * 0.6; // then resolve into the text
    const total = fillDur + descrambleDur;

    let raf;
    const run = () => {
      setDecoding(true);
      const start = performance.now();
      const tick = (now) => {
        const t = now - start;
        let out = "";
        if (t < fillDur) {
          // Stage 1: grow a string of random glyphs left-to-right.
          const n = Math.floor((t / fillDur) * L);
          for (let i = 0; i < n; i++) out += text[i] === " " ? " " : randGlyph();
        } else if (t < total) {
          // Stage 2: resolve left-to-right; unresolved stay as flickering glyphs.
          const revealed = Math.floor(((t - fillDur) / descrambleDur) * L);
          for (let i = 0; i < L; i++) {
            if (i < revealed) out += text[i];
            else out += text[i] === " " ? " " : randGlyph();
          }
        } else {
          out = text;
        }
        setDisplay(out);
        if (t < total) {
          raf = requestAnimationFrame(tick);
        } else {
          setDisplay(text);
          setDecoding(false);
        }
      };
      raf = requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !startedRef.current) {
          startedRef.current = true;
          timer = setTimeout(run, delay);
          io.disconnect();
        }
      },
      { threshold: 0.35 }
    );
    io.observe(el);

    return () => {
      io.disconnect();
      if (timer) clearTimeout(timer);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [text, duration, delay, ready]);

  return (
    <Tag ref={ref} className={className + " hud-scramble" + (decoding ? " is-decoding" : "")}>
      <span className="hud-scramble__ghost" aria-hidden="true">
        {text}
      </span>
      <span className="hud-scramble__layer">{display}</span>
    </Tag>
  );
}
